import { createReducer } from 'src/helpers/reducer';
import actionTypes from './actionTypes';
import { RATE_CARD_SIDES, RATE_CARD_UPLOAD_STATES } from '../constants';
import type { RateCardSideId } from '../constants';
import type { IRateCardCreateState } from '../types';

// Two consumers, not one: the wizard owns this slice, but the detail page also maps it to power its
// EMPTY-phase resume panels — so a change made "just for the wizard" reshapes that page too.

const emptyUploads = (): IRateCardCreateState['uploads'] => ({
  [RATE_CARD_SIDES.PAY]: null,
  [RATE_CARD_SIDES.BILL]: null,
});

const initState: IRateCardCreateState = {
  cardId: null,
  uploads: emptyUploads(),
  submitting: false,
  validations: null,
  downloadingTemplates: [],
};

type SidePayload = {
  payload: {
    side: RateCardSideId;
    token?: number;
    uploadId?: string;
    message?: string;
    retryable?: boolean;
    prominent?: boolean;
  };
};

const withSide = (
  state: IRateCardCreateState,
  side: RateCardSideId,
  upload: IRateCardCreateState['uploads'][RateCardSideId],
): IRateCardCreateState => ({
  ...state,
  uploads: { ...state.uploads, [side]: upload },
});

export default createReducer(initState, {
  // A `File` is not serialisable state, so the store keeps only what the panel renders: the name,
  // the attempt token and the progress. The component holds the `File` itself, for retries.
  [actionTypes.UPLOAD_SIDE_REQUEST]: (
    state: IRateCardCreateState,
    { payload }: { payload: { side: RateCardSideId; file: File; token: number } },
  ): IRateCardCreateState =>
    withSide(state, payload.side, {
      name: payload.file.name,
      token: payload.token,
      state: RATE_CARD_UPLOAD_STATES.UPLOADING,
    }),

  [actionTypes.UPLOAD_SIDE_SUCCESS]: (state: IRateCardCreateState, { payload }: SidePayload): IRateCardCreateState => {
    const current = state.uploads[payload.side];

    // Ignore an outcome for an attempt that has since been cleared or superseded.
    if (!current || current.token !== payload.token) return state;

    return withSide(state, payload.side, {
      ...current,
      state: RATE_CARD_UPLOAD_STATES.SUCCESS,
      uploadId: payload.uploadId,
    });
  },

  [actionTypes.UPLOAD_SIDE_ERROR]: (state: IRateCardCreateState, { payload }: SidePayload): IRateCardCreateState => {
    const current = state.uploads[payload.side];

    if (!current || current.token !== payload.token) return state;

    return withSide(state, payload.side, {
      name: current.name,
      token: current.token,
      state: RATE_CARD_UPLOAD_STATES.ERROR,
      message: payload.message,
      retryable: payload.retryable,
      prominent: payload.prominent,
    });
  },

  [actionTypes.CLEAR_SIDE]: (state: IRateCardCreateState, { payload }: SidePayload): IRateCardCreateState =>
    withSide(state, payload.side, null),

  // Tracked per side so two template downloads in flight each show their own progress.
  [actionTypes.DOWNLOAD_TEMPLATE_REQUEST]: (
    state: IRateCardCreateState,
    { payload }: SidePayload,
  ): IRateCardCreateState => ({
    ...state,
    downloadingTemplates: state.downloadingTemplates.includes(payload.side)
      ? state.downloadingTemplates
      : [...state.downloadingTemplates, payload.side],
  }),

  [actionTypes.DOWNLOAD_TEMPLATE_SETTLED]: (
    state: IRateCardCreateState,
    { payload }: SidePayload,
  ): IRateCardCreateState => ({
    ...state,
    downloadingTemplates: state.downloadingTemplates.filter(side => side !== payload.side),
  }),

  [actionTypes.CREATE_REQUEST]: (state: IRateCardCreateState): IRateCardCreateState => ({
    ...state,
    submitting: true,
    validations: null,
  }),

  [actionTypes.CREATE_SUCCESS]: (
    state: IRateCardCreateState,
    { payload }: { payload: { cardId: string } },
  ): IRateCardCreateState => ({
    ...state,
    cardId: payload.cardId,
    submitting: false,
    // A created card has no outstanding rejection. `CREATE_REQUEST` already clears this on the
    // path that exists today; clearing it here too keeps "created" and "rejected" mutually exclusive.
    validations: null,
  }),

  [actionTypes.CREATE_ERROR]: (
    state: IRateCardCreateState,
    { payload }: { payload: { validations: Record<string, string[]> | null } },
  ): IRateCardCreateState => ({
    ...state,
    submitting: false,
    validations: payload.validations,
  }),

  [actionTypes.CLEAR_STORE]: () => ({ ...initState, uploads: emptyUploads() }),
});
